import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUpload, faPlus, faTimes, faCheckCircle, faExclamationTriangle, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import './BulkUploadPage.css';

// For the data-loading effect: the hook's `t` changes with the language, and as an effect
// dependency it would reload the events each time the language is switched.
const tr = (key, options) => i18n.t(key, { ns: 'bulkUpload', ...options });

// The browser's own errors (fetch failing while offline, a reply that isn't JSON) are in English
// whatever language the page is in. English shows them as they are; Spanish shows its own text.
// Messages we threw ourselves, already translated, pass through.
const errorDetail = (err) => {
  const message = (err && err.message) || '';
  if (err instanceof TypeError && /fetch|network|load failed/i.test(message)) {
    return tr('errors.connection', { message });
  }
  if (err instanceof SyntaxError) {
    return tr('errors.unexpectedResponse', { message });
  }
  return message;
};

// "noviembre de 2026" → "Noviembre de 2026"; English month names are capitalized already.
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// Audit-style month-grouped event picker (single-select)
const EventPicker = ({ events, selectedEventId, onSelect }) => {
  const { t } = useTranslation('bulkUpload');
  const [collapsedMonths, setCollapsedMonths] = useState(new Set());

  const groupedEvents = useMemo(() => {
    const now = new Date();
    const groups = {};

    events.forEach(event => {
      if (!event.startDate) return;
      const eventDate = new Date(event.startDate);
      const sortKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;

      if (!groups[sortKey]) {
        // The month's name is written out when shown, in the admin's language.
        groups[sortKey] = { monthDate: eventDate, events: [], isFuture: eventDate >= now };
      }
      groups[sortKey].events.push({ ...event, eventDate });
    });

    Object.values(groups).forEach(group => {
      group.events.sort((a, b) => a.eventDate - b.eventDate);
    });

    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const aFuture = groups[a].isFuture;
      const bFuture = groups[b].isFuture;
      if (aFuture && bFuture) return a.localeCompare(b);
      if (!aFuture && !bFuture) return b.localeCompare(a);
      return aFuture ? -1 : 1;
    });

    return { groups, sortedKeys };
  }, [events]);

  const toggleMonth = (sortKey) => {
    setCollapsedMonths(prev => {
      const next = new Set(prev);
      if (next.has(sortKey)) next.delete(sortKey);
      else next.add(sortKey);
      return next;
    });
  };

  const selectedEvent = events.find(e => String(e.id) === String(selectedEventId));

  return (
    <div className="event-picker">
      {selectedEvent && (
        <div className="selected-event-badge">
          {selectedEvent.name}
          <button type="button" className="clear-event-btn" onClick={() => onSelect('')}>&times;</button>
        </div>
      )}
      <div className="event-filter-container">
        {groupedEvents.sortedKeys.map(sortKey => {
          const group = groupedEvents.groups[sortKey];
          const isCollapsed = collapsedMonths.has(sortKey);
          return (
            <div key={sortKey} className="event-month-group">
              <div className="month-header" onClick={() => toggleMonth(sortKey)}>
                <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} className="month-toggle-icon" />
                <strong>
                  {t('eventPicker.monthGroup', { icon: group.isFuture ? '📅' : '📌', month: capitalize(group.monthDate.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' })), value: group.events.length })}
                </strong>
              </div>
              {!isCollapsed && (
                <div className="event-list">
                  {group.events.map(event => (
                    <label key={event.id} className={`event-radio-label ${String(selectedEventId) === String(event.id) ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name={`event-picker-${Math.random()}`}
                        checked={String(selectedEventId) === String(event.id)}
                        onChange={() => onSelect(String(event.id))}
                      />
                      <span className="event-name">{event.name}</span>
                      <span className="event-meta">
                        {event.eventDate.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' })}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {events.length === 0 && <p className="no-events-msg">{t('eventPicker.noEvents')}</p>}
      </div>
    </div>
  );
};

const BulkUploadPage = () => {
  const { t } = useTranslation('bulkUpload');
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // Shared state
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('bulk');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Bulk upload state
  const [file, setFile] = useState(null);
  const [bulkEventId, setBulkEventId] = useState('');
  const [forceUpload, setForceUpload] = useState(false);

  // Manual entry state
  const [entries, setEntries] = useState([createEmptyEntry()]);
  const [manualForceUpload, setManualForceUpload] = useState(false);

  function createEmptyEntry() {
    return {
      id: Date.now() + Math.random(),
      eventId: '',
      iboNumber: '',
      teamName: '',
      customerName: '',
      quantity: 1,
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      showDropdown: false,
      showEventPicker: false,
      userData: null,
      validated: false,
      validatedViaAmway: false,
      validationError: null,
    };
  }

  // Fetch all events on mount (same as Audit page)
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const token = await getAccessToken();
        if (!token) return;

        const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/all`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.status === 'success') {
            setEvents(result.data || []);
          }
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(tr('errors.loadEvents'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [getAccessToken]);

  // --- Bulk Upload Handlers ---
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type !== 'text/csv') {
      setError(t('errors.csvOnly'));
      setFile(null);
      e.target.value = null;
    } else {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError(t('errors.selectFile')); return; }
    if (!bulkEventId) { setError(t('errors.selectEvent')); return; }

    try {
      setLoading(true);
      setError(null);
      setSuccessData(null);

      const token = await getAccessToken();
      if (!token) throw new Error(t('errors.noToken'));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('eventId', bulkEventId);
      formData.append('forceUpload', forceUpload);

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      if (result.status === 'success') {
        setSuccessData(result.data);
        setFile(null);
        document.getElementById('file-upload').value = '';
      } else {
        throw new Error(translateServerMessage(result.message, t('errors.uploadFallback')));
      }
    } catch (err) {
      console.error('Error uploading tickets:', err);
      setError(errorDetail(err) || t('errors.uploadError'));
    } finally {
      setLoading(false);
    }
  };

  // --- Manual Entry Handlers ---
  const updateEntry = (entryId, updates) => {
    setEntries(prev => prev.map(entry =>
      entry.id === entryId ? { ...entry, ...updates } : entry
    ));
  };

  const addEntry = () => {
    setEntries(prev => [...prev, createEmptyEntry()]);
  };

  const removeEntry = (entryId) => {
    setEntries(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(entry => entry.id !== entryId);
    });
  };

  const searchUsers = useCallback(async (query, entryId) => {
    if (!query || query.length < 2) {
      updateEntry(entryId, { searchResults: [], showDropdown: false });
      return;
    }

    updateEntry(entryId, { searchLoading: true });

    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/tickets/users/search?search=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (response.ok && result.status === 'success') {
        updateEntry(entryId, {
          searchResults: result.data || [],
          showDropdown: true,
          searchLoading: false
        });
      } else {
        updateEntry(entryId, { searchResults: [], showDropdown: false, searchLoading: false });
      }
    } catch (err) {
      console.error('Error searching users:', err);
      updateEntry(entryId, { searchResults: [], showDropdown: false, searchLoading: false });
    }
  }, [getAccessToken]);

  const debounceTimers = useRef({});

  const handleSearchInput = (entryId, value) => {
    updateEntry(entryId, {
      searchQuery: value,
      iboNumber: '',
      teamName: '',
      customerName: '',
      userData: null,
      validated: false,
      validatedViaAmway: false,
      validationError: null
    });

    if (debounceTimers.current[entryId]) {
      clearTimeout(debounceTimers.current[entryId]);
    }

    debounceTimers.current[entryId] = setTimeout(() => {
      searchUsers(value, entryId);
    }, 300);
  };

  const selectUser = async (entryId, user) => {
    updateEntry(entryId, {
      iboNumber: user.iboNumber,
      teamName: user.teamName || '',
      customerName: user.iboName || '',
      searchQuery: `${user.iboNumber} - ${user.iboName || ''}`,
      showDropdown: false,
      searchResults: [],
      validated: true,
      validatedViaAmway: false,
      validationError: null
    });

    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/get?iboNumber=${user.iboNumber}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (response.ok && result.status === 'success' && result.data) {
        updateEntry(entryId, {
          userData: result.data,
          teamName: result.data.teamName || user.teamName || '',
          customerName: result.data.iboName || user.iboName || ''
        });
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const validateIboViaAmway = async (entryId, iboNumber) => {
    if (!iboNumber || iboNumber.trim().length === 0) return;

    updateEntry(entryId, { searchLoading: true, validationError: null });

    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/validate-ticket-eligibility?iboNumber=${encodeURIComponent(iboNumber.trim())}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (response.ok && result.data) {
        if (result.data.eligible) {
          updateEntry(entryId, {
            iboNumber: iboNumber.trim(),
            validated: true,
            validatedViaAmway: true,
            validationError: null,
            searchLoading: false
          });
        } else {
          updateEntry(entryId, {
            validated: false,
            validatedViaAmway: false,
            validationError: translateServerMessage(result.data.message, t('errors.iboNotEligible')),
            searchLoading: false
          });
        }
      } else {
        updateEntry(entryId, {
          validated: false,
          validationError: t('errors.iboCouldNotValidate'),
          searchLoading: false
        });
      }
    } catch (err) {
      console.error('Error validating IBO:', err);
      updateEntry(entryId, {
        validated: false,
        validationError: t('errors.iboValidationFailed'),
        searchLoading: false
      });
    }
  };

  const handleSearchBlur = (entryId, entry) => {
    setTimeout(() => {
      updateEntry(entryId, { showDropdown: false });

      if (!entry.validated && entry.searchQuery && !entry.iboNumber) {
        const possibleIbo = entry.searchQuery.trim();
        if (/^\d+$/.test(possibleIbo)) {
          updateEntry(entryId, { iboNumber: possibleIbo });
          validateIboViaAmway(entryId, possibleIbo);
        }
      }
    }, 200);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    const validEntries = entries.filter(entry => entry.eventId && entry.iboNumber && entry.validated);
    if (validEntries.length === 0) {
      setError(t('errors.noValidEntries'));
      return;
    }

    const invalidEntries = entries.filter(entry => entry.eventId || entry.searchQuery);
    const notReady = invalidEntries.filter(entry => !entry.eventId || !entry.iboNumber || !entry.validated);
    if (notReady.length > 0) {
      setError(t('errors.incompleteEntries'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessData(null);

      const token = await getAccessToken();
      if (!token) throw new Error(t('errors.noToken'));

      const payload = {
        entries: validEntries.map(entry => ({
          eventId: parseInt(entry.eventId, 10),
          iboNumber: entry.iboNumber,
          teamName: entry.teamName || null,
          quantity: entry.quantity,
          customerName: entry.customerName || null
        })),
        forceUpload: manualForceUpload
      };

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/manual-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (result.status === 'success') {
        setSuccessData(result.data);
        setEntries([createEmptyEntry()]);
        setManualForceUpload(false);
      } else {
        throw new Error(translateServerMessage(result.message, t('errors.uploadFallback')));
      }
    } catch (err) {
      console.error('Error uploading tickets:', err);
      setError(errorDetail(err) || t('errors.uploadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    setSuccessData(null);
  };

  const getSelectedEventName = (eventId) => {
    const event = events.find(e => String(e.id) === String(eventId));
    return event ? event.name : '';
  };

  // --- Render ---
  return (
    <div className="bulk-upload-container">
      <div className="main-content">
        <button className="back-button" onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('backToDashboard')}
        </button>

        <div className="bulk-upload-header">
          <h2>{t('title')}</h2>
          <p>{t('subtitle')}</p>
        </div>

        <div className="tab-bar">
          <button
            className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => handleTabChange('bulk')}
          >
            {t('tabs.bulk')}
          </button>
          <button
            className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => handleTabChange('manual')}
          >
            {t('tabs.manual')}
          </button>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        {successData && (
          <div className="success-message">
            <p>{t('success.title')}</p>
            <div className="upload-stats">
              <p>{t('success.totalProcessed', { value: successData.totalProcessed })}</p>
              <p>{t('success.uploaded', { value: successData.uploadedCount })}</p>
              <p>{t('success.skipped', { value: successData.skippedCount })}</p>
              <p>{t('success.failed', { value: successData.failedCount })}</p>
            </div>
            {successData.entryResults && successData.entryResults.length > 0 && (
              <div className="entry-results-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('results.iboNumber')}</th>
                      <th>{t('results.event')}</th>
                      <th>{t('results.quantity')}</th>
                      <th>{t('results.status')}</th>
                      <th>{t('results.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {successData.entryResults.map((result, idx) => (
                      <tr key={idx} className={`result-row result-${result.status.toLowerCase()}`}>
                        <td>{result.iboNumber}</td>
                        <td>{result.eventName || t('results.eventFallback', { id: result.eventId })}</td>
                        <td>{result.quantity}</td>
                        <td>
                          <span className={`status-badge status-${result.status.toLowerCase()}`}>
                            {t(`entryStatus.${result.status}`, { defaultValue: result.status })}
                          </span>
                        </td>
                        <td>{result.reason ? translateServerMessage(result.reason) : t('results.noReason')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== Bulk Upload Tab ===== */}
        {activeTab === 'bulk' && (
          <div className="form-card">
            <form className="upload-form" onSubmit={handleBulkSubmit}>
              <div className="form-group">
                <label>{t('bulk.event')}</label>
                <EventPicker
                  events={events}
                  selectedEventId={bulkEventId}
                  onSelect={setBulkEventId}
                />
              </div>

              <div className="form-group">
                <label htmlFor="file-upload">{t('bulk.file')}</label>
                <input
                  type="file"
                  id="file-upload"
                  accept=".csv"
                  onChange={handleFileChange}
                  required
                />
                <p className="input-helper">{t('bulk.fileHelper')}</p>
              </div>

              <div className="form-group toggle-group">
                <label htmlFor="force-upload">{t('bulk.forceUpload')}</label>
                <div className="toggle-switch-wrapper">
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="force-upload"
                      checked={forceUpload}
                      onChange={() => setForceUpload(!forceUpload)}
                    />
                    <label htmlFor="force-upload" className="toggle-label"></label>
                  </div>
                </div>
                <p className="input-helper">{t('bulk.forceUploadHelper')}</p>
              </div>

              <button type="submit" className="upload-button" disabled={loading || !bulkEventId}>
                {loading ? t('bulk.uploading') : (
                  <><FontAwesomeIcon icon={faUpload} /> {t('bulk.submit')}</>
                )}
              </button>
            </form>
          </div>
        )}

        {/* ===== Manual Entry Tab ===== */}
        {activeTab === 'manual' && (
          <div className="form-card manual-entry-card">
            <form className="upload-form" onSubmit={handleManualSubmit}>
              {entries.map((entry, index) => (
                <div key={entry.id} className="manual-entry-block">
                  <div className="entry-row-header">
                    <span className="entry-number">{t('manual.entry', { number: index + 1 })}</span>
                    {entries.length > 1 && (
                      <button
                        type="button"
                        className="remove-entry-button"
                        onClick={() => removeEntry(entry.id)}
                        title={t('manual.removeEntry')}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>

                  {/* Event Picker */}
                  <div className="entry-event-picker">
                    <label>{t('manual.event')}</label>
                    {entry.eventId && (
                      <div className="selected-event-inline">
                        {getSelectedEventName(entry.eventId)}
                        <button type="button" className="clear-event-btn" onClick={() => updateEntry(entry.id, { eventId: '' })}>&times;</button>
                      </div>
                    )}
                    <button
                      type="button"
                      className="event-picker-toggle"
                      onClick={() => updateEntry(entry.id, { showEventPicker: !entry.showEventPicker })}
                    >
                      {entry.eventId ? t('manual.changeEvent') : t('manual.selectEvent')}
                      <FontAwesomeIcon icon={entry.showEventPicker ? faChevronDown : faChevronRight} />
                    </button>
                    {entry.showEventPicker && (
                      <EventPicker
                        events={events}
                        selectedEventId={entry.eventId}
                        onSelect={(eventId) => updateEntry(entry.id, { eventId, showEventPicker: false })}
                      />
                    )}
                  </div>

                  {/* IBO + Qty row */}
                  <div className="entry-row">
                    <div className="entry-field ibo-field">
                      <label>{t('manual.ibo')}</label>
                      <div className="autocomplete-container">
                        <input
                          type="text"
                          placeholder={t('manual.iboPlaceholder')}
                          value={entry.searchQuery}
                          onChange={(e) => handleSearchInput(entry.id, e.target.value)}
                          onBlur={() => handleSearchBlur(entry.id, entry)}
                          onFocus={() => {
                            if (entry.searchResults.length > 0) {
                              updateEntry(entry.id, { showDropdown: true });
                            }
                          }}
                          className={entry.validationError ? 'input-error' : entry.validated ? 'input-valid' : ''}
                        />
                        {entry.searchLoading && <span className="search-spinner"></span>}
                        {entry.validated && !entry.searchLoading && (
                          <FontAwesomeIcon icon={faCheckCircle} className="validation-icon valid" />
                        )}
                        {entry.validationError && !entry.searchLoading && (
                          <FontAwesomeIcon icon={faExclamationTriangle} className="validation-icon invalid" />
                        )}
                        {entry.showDropdown && entry.searchResults.length > 0 && (
                          <div className="autocomplete-dropdown">
                            {entry.searchResults.map((user, idx) => (
                              <div
                                key={idx}
                                className="autocomplete-item"
                                onMouseDown={() => selectUser(entry.id, user)}
                              >
                                <span className="autocomplete-ibo">{user.iboNumber}</span>
                                <span className="autocomplete-name">{user.iboName || t('notAvailable')}</span>
                                {user.teamName && (
                                  <span className="autocomplete-team">{user.teamName}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {entry.validationError && (
                        <p className="field-error">{entry.validationError}</p>
                      )}
                      {entry.validatedViaAmway && (
                        <p className="amway-badge">{t('manual.validatedViaAmway')}</p>
                      )}
                    </div>

                    <div className="entry-field qty-field">
                      <label>{t('manual.quantity')}</label>
                      <input
                        type="number"
                        min="1"
                        value={entry.quantity}
                        onChange={(e) => updateEntry(entry.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                      />
                    </div>
                  </div>

                  {/* User Detail Card */}
                  {entry.userData && (
                    <div className="user-detail-card">
                      <div className="user-detail-row">
                        <span className="user-detail-label">{t('manual.name')}</span>
                        <span className="user-detail-value">{entry.userData.iboName || entry.userData.name || t('notAvailable')}</span>
                      </div>
                      <div className="user-detail-row">
                        <span className="user-detail-label">{t('manual.iboNumber')}</span>
                        <span className="user-detail-value">{entry.userData.iboNumber}</span>
                      </div>
                      <div className="user-detail-row">
                        <span className="user-detail-label">{t('manual.team')}</span>
                        <span className="user-detail-value">{entry.userData.teamName || t('notAvailable')}</span>
                      </div>
                      <div className="user-detail-row">
                        <span className="user-detail-label">{t('manual.uplinePlatinum')}</span>
                        <span className="user-detail-value">{entry.userData.uplinePlatinumIbo || t('notAvailable')}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <button type="button" className="add-entry-button" onClick={addEntry}>
                <FontAwesomeIcon icon={faPlus} /> {t('manual.addEntry')}
              </button>

              <div className="form-group toggle-group">
                <label htmlFor="manual-force-upload">{t('bulk.forceUpload')}</label>
                <div className="toggle-switch-wrapper">
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="manual-force-upload"
                      checked={manualForceUpload}
                      onChange={() => setManualForceUpload(!manualForceUpload)}
                    />
                    <label htmlFor="manual-force-upload" className="toggle-label"></label>
                  </div>
                </div>
                <p className="input-helper">{t('bulk.forceUploadHelper')}</p>
              </div>

              <button
                type="submit"
                className="upload-button"
                disabled={loading || !entries.some(e => e.eventId && e.iboNumber && e.validated)}
              >
                {loading ? t('bulk.uploading') : (
                  <><FontAwesomeIcon icon={faUpload} /> {t('bulk.submit')}</>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadPage;
